import type { BackofficeGroups } from '@backoffice-frontend/user-management-ui'

type RightNamesByAreas =
  | 'AREA_EMPLOYEES'
  | 'AREA_VEHICLES'
  | 'AREA_VEHICLE_GROUPS'
  | 'AREA_CUSTOM_ROAD_BLOCKAGES'
  | 'AREA_CUSTOMERS'
  | 'AREA_SERVICE_AREA'
  | 'AREA_ALERTS_TESTING'

export const getGroupsByRight = (groups: BackofficeGroups, rightName: string) =>
  groups.filter(group => group.rights?.some(right => right === rightName))

export const getAllRightsFromGroups = (groups: BackofficeGroups): string[] => {
  const allRights = new Set<string>()
  groups.forEach(group => {
    group.rights?.forEach(right => allRights.add(right))
  })
  return Array.from(allRights).sort()
}

export const getRelatedRights = (
  allRights: string[],
  subpage: string,
  areaRight?: string,
): string[] => {
  if (!subpage) return []

  // Handle both main pages and subpages
  const isSubpage = subpage.indexOf('-') !== -1
  const mainSection = subpage.split('-')[0].toUpperCase()

  let allKeywords: string[]
  let keywords: string[]

  if (isSubpage) {
    const subpageParts = subpage.split('-').slice(1) // Remove main section
    keywords = subpageParts.map(part => part.toUpperCase())
    allKeywords = [mainSection, ...keywords]
  } else {
    // For main pages, create variations of the page name
    // Since AREA_ rights are for main pages, we look for related non-AREA rights
    const pageKeywords = [
      mainSection, // e.g., "DISRUPTIONS"
      mainSection.endsWith('S') ? mainSection.slice(0, -1) : mainSection + 'S', // handle plural/singular
    ]
    keywords = pageKeywords
    allKeywords = pageKeywords
  }

  const matchedRights = allRights.filter(right => {
    // Skip the area right so we don't show it twice
    if (areaRight && right === areaRight) {
      return false
    }

    // Check if any keyword appears in the right name
    const hasKeyword = allKeywords.some(keyword => right.includes(keyword))

    // Check for rights with AREA in different positions (middle, end)
    const hasAreaPattern = right.includes('AREA')

    // Check for conceptually related patterns
    const conceptualMatch = allKeywords.some(keyword => {
      // Handle plurals and variations
      const variations = [
        keyword,
        keyword + 'S', // plural
        keyword.slice(0, -1), // singular if keyword ends with S
      ]
      return variations.some(variation => right.includes(variation))
    })

    const matches = hasKeyword || (hasAreaPattern && conceptualMatch)

    return matches
  })

  return matchedRights
}

export const getPathName = (): RightNamesByAreas | string => {
  const href = window.location.pathname.split('/').filter(Boolean)
  if (href[0] === 'sam') {
    // For SAM paths, get the main section and subpage if exists
    return href.length > 2 ? `${href[1]}-${href[2]}` : href[1]
  }
  // For regular paths, get the main section and subpage if exists
  return href.length > 1 ? `${href[0]}-${href[1]}` : href[0]
}

const createPathVariations = (rightName: string): string[] => {
  // Convert AREA_SOME_RIGHT to possible path variations
  const withoutArea = rightName.replace('AREA_', '')
  const parts = withoutArea.split('_')

  const variations = [
    // kebab-case: AREA_SOME_RIGHT -> some-right
    parts
      .map(part => part.toLowerCase())
      .join('-'),
    // singular form: AREA_VEHICLES -> vehicle
    parts
      .map(part => {
        const lower = part.toLowerCase()
        return lower.endsWith('s') ? lower.slice(0, -1) : lower
      })
      .join('-'),
  ]

  // Add single word variations for single-part rights
  if (parts.length === 1) {
    const singlePart = parts[0].toLowerCase()
    variations.push(singlePart)
    if (singlePart.endsWith('s')) {
      variations.push(singlePart.slice(0, -1))
    }
  }

  return [...new Set(variations)] // Remove duplicates
}

export const getRightName = (allRights?: string[]) => {
  const pathName = getPathName()

  // If we don't have allRights, fall back to constructing the right name
  if (!allRights) {
    const constructedRightName = `AREA_${pathName
      .split('-')
      .map(e => e.toUpperCase())
      .join('_')}`
    return constructedRightName
  }

  // Get all AREA rights and create a mapping
  const areaRights = allRights.filter(right => right.startsWith('AREA_'))

  // Try to find a direct match by checking path variations against each right
  for (const right of areaRights) {
    const pathVariations = createPathVariations(right)

    // Check if current path matches any variation of this right
    if (pathVariations.includes(pathName)) {
      return right
    }

    // For subpages, also check if parent section matches
    const parentSection = pathName.split('-')[0]
    if (pathVariations.includes(parentSection)) {
      return right
    }
  }

  // If no direct match, try fuzzy matching with keywords
  const pageKeywords = pathName.split('-').map(part => part.toUpperCase())

  const matchingAreaRights = areaRights.filter(right =>
    pageKeywords.some(keyword => {
      // Handle plural/singular variations
      const variations = [
        keyword,
        keyword + 'S',
        keyword.endsWith('S') ? keyword.slice(0, -1) : keyword,
      ]
      return variations.some(variation => right.includes(variation))
    }),
  )

  if (matchingAreaRights.length > 0) {
    return matchingAreaRights[0] // Return first match
  }

  // Fallback - construct the right name but it might not exist
  const constructedRightName = `AREA_${pathName
    .split('-')
    .map(e => e.toUpperCase())
    .join('_')}`

  return constructedRightName
}

export const logPageAccessRequirements = (data: BackofficeGroups) => {
  const allRights = getAllRightsFromGroups(data)
  const rightName = getRightName(allRights)
  const currentPath = getPathName()
  const groups = getGroupsByRight(data, rightName)
  const fullPath = window.location.pathname
  const relatedRights = getRelatedRights(allRights, currentPath, rightName)

  // Check if the right actually exists in the available rights
  const rightExists = allRights.includes(rightName)

  console.info('=== PAGE ACCESS REQUIREMENTS ===')
  console.info(`Full URL path: ${fullPath}`)
  console.info(`Parsed path: ${currentPath}`)
  console.info(`Area right: ${rightName}`)

  if (!rightExists) {
    console.warn(
      `⚠️  WARNING: Area right "${rightName}" does not exist in available rights!`,
    )
    console.info(
      'Available AREA rights:',
      allRights.filter(right => right.includes('AREA')).sort(),
    )
  } else {
    console.info(`✅ Area right "${rightName}" exists and is valid`)
  }

  if (relatedRights.length > 0) {
    console.info(`Additional rights that might be needed:`, relatedRights)
  } else {
    console.info(`No additional rights detected for this subpage`)
  }

  console.info(
    `Groups with area right "${rightName}":`,
    groups.map(g => ({
      name: g.name,
      displayName: g.displayName,
      rights: g.rights,
    })),
  )
  console.info(`Total groups with area right: ${groups.length}`)

  if (groups.length === 0) {
    console.info(`⚠️  No groups found with area right: ${rightName}`)
    if (!rightExists) {
      console.info(
        `This is likely because the right "${rightName}" does not exist.`,
      )
    } else {
      console.info(`The right exists but no groups have this permission.`)
    }
  }

  console.info('================================')
}
